import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetChatbotResponseMutation } from '@Store/api/chatbotApiSlice';
import dayjs from 'dayjs';
import { ChatbotButton } from './components/ChatbotButton';
import { ChatbotDrawer } from './components/ChatbotDrawer';
import { ChatbotMessage } from './components/ChatbotMessage';

import styles from './chatbot.module.css';

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 0,
      message: 'Hello, how can I help you today?',
      time: dayjs().format('MM/DD/YYYY, h:mm A'),
      isUser: false,
    },
  ]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [getChatBotResponse, { isLoading }] = useGetChatbotResponseMutation();

  const fetchChat = useCallback(
    async (text: string): Promise<void> => {
      const response = await getChatBotResponse(text);

      if ('error' in response) {
        console.error(response.error);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            message: response.data.output.output,
            time: dayjs().format('MM/DD/YYYY, h:mm A'),
            isUser: false,
          },
        ]);
      }
    },
    [getChatBotResponse]
  );

  const handleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
      if (e.key === 'Enter') {
        e.preventDefault();

        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            message: question,
            time: dayjs().format('MM/DD/YYYY, h:mm A'),
            isUser: true,
          },
        ]);
        fetchChat(question);
        setQuestion('');
      }
    },
    [question, fetchChat]
  );

  useEffect(() => {
    if (textareaRef.current) {
      // To make textarea auto-growing
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [question]);

  return (
    <>
      <ChatbotButton onClick={handleOpen} />

      <ChatbotDrawer
        isOpen={isOpen}
        onClose={handleOpen}
        footer={
          <textarea
            className={styles.textarea}
            ref={textareaRef}
            placeholder="Enter your message..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleSubmit}
            rows={1}
          />
        }
      >
        {messages.map(({ id, message, time, isUser }) => (
          <ChatbotMessage
            key={id}
            message={message}
            time={time}
            isUser={isUser}
          />
        ))}
        {isLoading && <ChatbotMessage message="..." isUser={false} />}
      </ChatbotDrawer>
    </>
  );
};

export default Chatbot;
